<script lang="ts">
  import { untrack } from 'svelte';
  import ChatHeader from '../../lib/chat/ChatHeader.svelte';
  import ConfirmModal from '../../lib/common/ConfirmModal.svelte';
  import { getRoute, navigate } from '../../route.svelte';
  import type { Palette } from '../../theme.svelte';
  import * as api from './api';
  import {
    abrirAlta,
    activarSeguimiento,
    alta,
    cambiarOrden,
    cancelarActivacion,
    cargarMasActuacionesPreview,
    cargarResultados,
    cerrarAlta,
    consultar,
    irPaginaResultados,
    paginasDisponibles,
    reintentarConsulta,
    reintentarExpansionPreview,
    RESULTADOS_POR_PAGINA,
    seguirProcesoPreview,
    toggleProcesoPreview,
  } from './estadoAlta.svelte';
  import { agruparSujetosProcesales } from './involucrados';
  import type {
    Coincidencia,
    Seguimiento,
    SeguimientoDetalle,
    SeguimientoListResponse,
    ProcesoDetalleResponse,
  } from './types';

  type SeguimientoPageProps = {
    onToggleSidebar: () => void;
    palette: Palette;
    isSidebarCollapsed?: boolean;
    isMobile?: boolean;
  };

  let {
    onToggleSidebar,
    palette,
    isSidebarCollapsed = false,
    isMobile = false,
  }: SeguimientoPageProps = $props();

  let route = $derived(getRoute());

  // ── Listado ────────────────────────────────────────────────
  let listado = $state<SeguimientoListResponse | null>(null);
  let listLoading = $state(true);
  let listError = $state<string | null>(null);
  let activeTab = $state<'procesos' | 'nombres'>('procesos');

  // ── Alta guiada (estado compartido, sobrevive a la navegación) ──
  const consulta = $derived(alta.consulta);
  const expansion = $derived(alta.expansion);
  const activacion = $derived(alta.activacion);

  // Resultados ya guardados: página actual y navegación (20 por página).
  const resultados = $derived(alta.resultados);
  const paginasResultados = $derived(paginasDisponibles());
  const paginaResultados = $derived(
    Math.min(alta.resultadosPagina, paginasDisponibles()),
  );
  const inicioResultados = $derived(
    resultados.length > 0 ? (paginaResultados - 1) * RESULTADOS_POR_PAGINA + 1 : 0,
  );
  const finResultados = $derived(
    resultados.length > 0 ? inicioResultados + resultados.length - 1 : 0,
  );
  const totalResultados = $derived(
    Math.max(consulta?.resultados ?? 0, alta.resultadosTotal),
  );
  const esperandoMas = $derived(
    Boolean(consulta?.en_curso) && paginaResultados >= paginasResultados,
  );

  // ── Detalle ────────────────────────────────────────────────
  let detalle = $state<SeguimientoDetalle | null>(null);
  let detalleLoading = $state(false);
  let detalleError = $state<string | null>(null);
  let procesoAbierto = $state<number | null>(null);
  let procesoDetalle = $state<ProcesoDetalleResponse | null>(null);
  let procesoLoading = $state(false);
  let seguirBusy = $state<number | null>(null);

  // ── Acciones ───────────────────────────────────────────────
  let accionBusy = $state<number | null>(null);
  let aviso = $state<string | null>(null);
  let confirmarRetiro = $state<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  });

  const dateFmt = new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium' });
  const dateTimeFmt = new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Bogota',
  });

  let seguimientos = $derived(listado?.seguimientos ?? []);
  let deRadicado = $derived(seguimientos.filter((s) => s.tipo === 'radicado'));
  let deNombre = $derived(seguimientos.filter((s) => s.tipo === 'nombre'));
  let limiteAlcanzado = $derived(
    listado !== null && listado.total >= listado.maximo,
  );

  // ── Formato ────────────────────────────────────────────────

  function fecha(valor: string | null | undefined): string {
    if (!valor) return '—';
    const normalizado = valor.length === 10 ? `${valor}T12:00:00` : valor;
    const fechaObj = new Date(normalizado);
    if (Number.isNaN(fechaObj.valueOf())) return valor;
    return valor.length === 10 ? dateFmt.format(fechaObj) : dateTimeFmt.format(fechaObj);
  }

  function mensajeError(error: unknown): string {
    return api.mensajeError(error);
  }

  function mostrarAviso(texto: string): void {
    aviso = texto;
    setTimeout(() => {
      aviso = null;
    }, 5000);
  }

  function estadoLabel(seg: Seguimiento): string {
    if (seg.pausado) return 'Pausado';
    if (seg.activacion_pendiente) return 'Preparando';
    if (!seg.establecido_en) return 'Inicializando';
    if (!seg.estado.base_establecida) return 'Inicializando';
    if (seg.estado.fallos_consecutivos > 0) return 'Con problemas';
    if (seg.estado.revision_incompleta) return 'Revisión incompleta';
    return 'Activo';
  }

  function estadoClase(seg: Seguimiento): string {
    if (seg.pausado) return 'off';
    if (
      seg.activacion_pendiente ||
      !seg.estado.base_establecida ||
      !seg.establecido_en ||
      seg.estado.fallos_consecutivos > 0 ||
      seg.estado.revision_incompleta
    ) {
      return 'warn';
    }
    return 'ok';
  }

  function estadoDetalle(seg: Seguimiento): string {
    if (seg.activacion_pendiente) {
      return 'Preparando seguimiento · Completando la consulta inicial…';
    }
    if (!seg.estado.base_establecida || !seg.establecido_en) {
      return 'Preparando la primera consulta completa…';
    }
    if (seg.estado.ultima_revision_exitosa) {
      return `Última revisión exitosa: ${fecha(seg.estado.ultima_revision_exitosa)}`;
    }
    return 'Aún sin revisión exitosa.';
  }

  // ── Carga ──────────────────────────────────────────────────

  async function loadList(): Promise<void> {
    listLoading = true;
    listError = null;
    try {
      listado = await api.listSeguimientos();
    } catch (error) {
      listError = mensajeError(error);
    } finally {
      listLoading = false;
    }
  }

  async function loadDetalle(id: number): Promise<void> {
    detalleLoading = true;
    detalleError = null;
    detalle = null;
    procesoAbierto = null;
    procesoDetalle = null;
    try {
      detalle = await api.getSeguimiento(id);
    } catch (error) {
      detalleError = mensajeError(error);
    } finally {
      detalleLoading = false;
    }
  }

  $effect(() => {
    const activeRoute = route;
    untrack(() => {
      if (activeRoute.name === 'seguimiento') {
        void loadList();
      } else if (activeRoute.name === 'seguimiento-detalle') {
        void loadDetalle(activeRoute.seguimientoId);
      }
    });
  });

  // ── Alta ───────────────────────────────────────────────────

  function puedeActivar(): boolean {
    const actual = alta.consulta;
    if (!actual || alta.activarBusy || alta.seguirBusy) return false;
    if (actual.estado === 'fallida' || actual.estado === 'cancelada') return false;
    return true;
  }

  function seguimientoDeRadicado(radicadoDigitos: string): Seguimiento | undefined {
    return seguimientos.find(
      (seg) => seg.tipo === 'radicado' && seg.clave === radicadoDigitos,
    );
  }

  // La creación en segundo plano (activar o seguir) navega al detalle cuando
  // termina, aunque la persona haya cambiado de sección mientras tanto.
  $effect(() => {
    const respuesta = alta.creado;
    if (!respuesta) return;
    alta.creado = null;
    cerrarAlta();
    navigate(`/seguimiento-judicial/seguimiento/${respuesta.seguimiento.id}`);
  });

  $effect(() => {
    const respuesta = alta.seguido;
    if (!respuesta) return;
    alta.seguido = null;
    navigate(`/seguimiento-judicial/seguimiento/${respuesta.seguimiento.id}`);
  });

  // ── Acciones sobre seguimientos ────────────────────────────

  function reemplazar(actualizado: Seguimiento): void {
    if (listado) {
      listado = {
        ...listado,
        seguimientos: listado.seguimientos.map((seg) =>
          seg.id === actualizado.id ? { ...seg, ...actualizado } : seg,
        ),
      };
    }
    if (detalle && detalle.id === actualizado.id) {
      detalle = { ...detalle, ...actualizado };
    }
  }

  async function togglePausa(seg: Seguimiento): Promise<void> {
    accionBusy = seg.id;
    try {
      reemplazar(await api.actualizarSeguimiento(seg.id, { pausado: !seg.pausado }));
      mostrarAviso(seg.pausado ? 'Seguimiento reanudado.' : 'Seguimiento pausado.');
    } catch (error) {
      listError = mensajeError(error);
    } finally {
      accionBusy = null;
    }
  }

  async function toggleCorreo(seg: Seguimiento): Promise<void> {
    accionBusy = seg.id;
    try {
      reemplazar(
        await api.actualizarSeguimiento(seg.id, { correo_activo: !seg.correo_activo }),
      );
      mostrarAviso(
        seg.correo_activo
          ? 'Alertas por correo desactivadas.'
          : 'Alertas por correo activadas.',
      );
    } catch (error) {
      listError = mensajeError(error);
    } finally {
      accionBusy = null;
    }
  }

  async function revisar(seg: Seguimiento): Promise<void> {
    accionBusy = seg.id;
    try {
      await api.revisarSeguimiento(seg.id);
      mostrarAviso('Revisión solicitada. Los resultados aparecerán en unos minutos.');
    } catch (error) {
      listError = mensajeError(error);
    } finally {
      accionBusy = null;
    }
  }

  function pedirRetiro(seg: Seguimiento): void {
    confirmarRetiro = { open: true, id: seg.id };
  }

  async function confirmarRetiroAccion(): Promise<void> {
    const id = confirmarRetiro.id;
    confirmarRetiro = { open: false, id: null };
    if (!id) return;
    try {
      await api.eliminarSeguimiento(id);
      if (detalle?.id === id) {
        detalle = null;
        navigate('/seguimiento-judicial');
      }
      await loadList();
      mostrarAviso('Seguimiento retirado.');
    } catch (error) {
      listError = mensajeError(error);
    }
  }

  // ── Detalle: procesos ──────────────────────────────────────

  async function toggleProceso(procesoId: number): Promise<void> {
    if (procesoAbierto === procesoId) {
      procesoAbierto = null;
      procesoDetalle = null;
      return;
    }
    procesoAbierto = procesoId;
    procesoDetalle = null;
    procesoLoading = true;
    try {
      procesoDetalle = await api.getProceso(procesoId, 1);
    } catch (error) {
      detalleError = mensajeError(error);
    } finally {
      procesoLoading = false;
    }
  }

  async function cargarMasActuaciones(): Promise<void> {
    if (!procesoDetalle) return;
    procesoLoading = true;
    try {
      const siguiente = await api.getProceso(
        procesoDetalle.proceso.id,
        procesoDetalle.pagina + 1,
      );
      procesoDetalle = {
        ...siguiente,
        actuaciones: [...procesoDetalle.actuaciones, ...siguiente.actuaciones],
      };
    } catch (error) {
      detalleError = mensajeError(error);
    } finally {
      procesoLoading = false;
    }
  }

  async function seguirEsteProceso(coincidencia: Coincidencia): Promise<void> {
    if (!detalle) return;
    seguirBusy = coincidencia.proceso.id;
    try {
      const respuesta = await api.seguirProceso(detalle.id, coincidencia.proceso.id);
      navigate(`/seguimiento-judicial/seguimiento/${respuesta.seguimiento.id}`);
    } catch (error) {
      detalleError = mensajeError(error);
    } finally {
      seguirBusy = null;
    }
  }

  function volver(): void {
    navigate('/seguimiento-judicial');
  }
</script>

<svelte:head>
  <title>Seguimiento judicial | Jurismart</title>
</svelte:head>

<div
  class="seguimiento-layout"
  style={`--seg-surface: ${palette.bgCard}; --seg-input: ${palette.bgChatInput}; --seg-text: ${palette.textPrimary}; --seg-muted: ${palette.textSecondary}; --seg-border: ${palette.borderSoft}; --seg-accent: ${palette.accent};`}
>
  <ChatHeader
    {onToggleSidebar}
    {palette}
    {isSidebarCollapsed}
    {isMobile}
    heading="Seguimiento judicial"
  />

  <main class="seguimiento-content scroll-y">
    <div class="seguimiento-container">
      {#if aviso}
        <div class="aviso" role="status">{aviso}</div>
      {/if}

      {#if route.name === 'seguimiento'}
        <!-- ─────────────── LISTADO ─────────────── -->
        <section class="intro" aria-label="Descripción del módulo">
          <p class="eyebrow">Alertas de procesos</p>
          <p>
            Sigue un proceso por su radicado y recibe avisos cuando tenga actuaciones
            nuevas; vigila un nombre para enterarte de procesos que aparezcan con esa
            coincidencia. Los avisos por correo no reemplazan las notificaciones
            oficiales de la Rama Judicial.
          </p>
        </section>

        <div class="tabs" role="tablist" aria-label="Tipos de seguimiento">
          <button
            class="tab"
            class:active={activeTab === 'procesos'}
            type="button"
            role="tab"
            aria-selected={activeTab === 'procesos'}
            onclick={() => (activeTab = 'procesos')}
          >
            Procesos ({deRadicado.length})
          </button>
          <button
            class="tab"
            class:active={activeTab === 'nombres'}
            type="button"
            role="tab"
            aria-selected={activeTab === 'nombres'}
            onclick={() => (activeTab = 'nombres')}
          >
            Nombres vigilados ({deNombre.length})
          </button>
        </div>

        <div class="acciones-alta">
          <button
            class="button button-primary"
            type="button"
            disabled={limiteAlcanzado}
            onclick={() => abrirAlta('radicado')}
          >
            <i class="bi bi-hash" aria-hidden="true"></i>
            Seguir proceso
          </button>
          <button
            class="button button-secondary"
            type="button"
            disabled={limiteAlcanzado}
            onclick={() => abrirAlta('nombre')}
          >
            <i class="bi bi-person-lines-fill" aria-hidden="true"></i>
            Vigilar nombre
          </button>
          {#if limiteAlcanzado}
            <span class="limite-nota">
              Alcanzaste el máximo de seguimientos. Retira alguno para crear otro.
            </span>
          {/if}
        </div>

        {#if alta.tipo}
          <section class="crear-panel" aria-label="Nuevo seguimiento">
            <div class="crear-head">
              <h2>
                {alta.tipo === 'radicado'
                  ? 'Seguir un proceso por radicado'
                  : 'Vigilar un nombre'}
              </h2>
              <button
                class="cerrar"
                type="button"
                disabled={alta.activarBusy || alta.seguirBusy}
                onclick={cerrarAlta}
                aria-label="Cerrar"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>

            <form class="crear-form" onsubmit={consultar}>
              <label class="field">
                <span class="field-label">
                  {alta.tipo === 'radicado'
                    ? 'Número de radicado (23 dígitos)'
                    : 'Nombre completo (nombre y apellidos)'}
                </span>
                <input
                  bind:value={alta.valor}
                  type="text"
                  autocomplete="off"
                  placeholder={alta.tipo === 'radicado'
                    ? '11001-11-02-000-2012-01870-00'
                    : 'Juan Pérez Gómez'}
                />
              </label>
              <button
                class="button button-primary"
                type="submit"
                disabled={alta.consultaLoading}
              >
                <i class="bi bi-search" aria-hidden="true"></i>
                Consultar
              </button>
            </form>

            {#if alta.tipo === 'nombre'}
              <p class="nota-homonimos">
                <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
                Una coincidencia de nombre no confirma que el proceso pertenezca a la
                persona que buscas: puede tratarse de otra persona con un nombre igual o
                similar.
              </p>
            {/if}

            {#if alta.consultaMensaje}
              <p class="estado-texto error">{alta.consultaMensaje}</p>
            {:else if alta.consultaError}
              <p class="estado-texto error">{alta.consultaError}</p>
            {:else if alta.consultaLoading && !consulta}
              <p class="estado-texto">Iniciando consulta…</p>
            {:else if consulta}
              {#if consulta.en_curso}
                <p class="estado-texto">
                  {#if consulta.resultados > 0}
                    {consulta.resultados}{consulta.total_informado
                      ? ` de ${consulta.total_informado}` : ''}
                    {consulta.resultados === 1
                      ? 'coincidencia disponible' : 'coincidencias disponibles'}
                    · Seguimos consultando.
                  {:else}
                    Consultando la Rama Judicial…
                  {/if}
                </p>
                <p class="nota-proceso">
                  Puede tardar; la consulta sigue en curso aunque cambies de
                  sección y te avisamos al terminar.
                </p>
              {:else if consulta.estado === 'completa'}
                <p class="estado-texto">
                  {consulta.resultados}
                  {consulta.resultados === 1
                    ? 'resultado encontrado.'
                    : 'resultados encontrados.'}
                </p>
              {:else if consulta.estado === 'parcial'}
                <p class="estado-texto error">
                  Consulta interrumpida · {consulta.resultados}
                  {consulta.resultados === 1
                    ? 'resultado disponible.' : 'resultados disponibles.'}
                </p>
                {#if consulta.error}
                  <p class="nota-proceso">{consulta.error}</p>
                {/if}
              {:else if consulta.estado === 'fallida'}
                <p class="estado-texto error">
                  {consulta.error || 'No se pudo consultar la Rama Judicial.'}
                </p>
              {:else if consulta.estado === 'cancelada'}
                <p class="estado-texto error">
                  La consulta fue cancelada por una búsqueda nueva.
                </p>
              {/if}

              {#if consulta.estado === 'parcial' || consulta.estado === 'fallida'}
                <div class="acciones-consulta">
                  <button
                    class="button button-secondary"
                    type="button"
                    disabled={alta.consultaLoading}
                    onclick={reintentarConsulta}
                  >
                    <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                    {alta.consultaLoading ? 'Reintentando…' : 'Reintentar consulta'}
                  </button>
                </div>
              {/if}

              {#if consulta.resultados > 0 && consulta.estado !== 'cancelada'}
                {#if alta.resultadosError}
                  <p class="estado-texto error">{alta.resultadosError}</p>
                  <div class="acciones-consulta">
                    <button
                      class="button button-ghost"
                      type="button"
                      onclick={() => cargarResultados(alta.resultadosPagina)}
                    >
                      Reintentar
                    </button>
                  </div>
                {:else if alta.resultadosLoading && resultados.length === 0}
                  <p class="estado-texto">Cargando resultados…</p>
                {:else}
                  {#if consulta.completa}
                    <div class="orden-selector" role="group" aria-label="Orden de los resultados">
                      <span class="orden-etiqueta">Ordenar por</span>
                      <button
                        class="orden-opcion"
                        class:active={alta.orden === 'estable'}
                        type="button"
                        aria-pressed={alta.orden === 'estable'}
                        onclick={() => cambiarOrden('estable')}
                      >
                        Orden de consulta
                      </button>
                      <button
                        class="orden-opcion"
                        class:active={alta.orden === 'actuacion'}
                        type="button"
                        aria-pressed={alta.orden === 'actuacion'}
                        onclick={() => cambiarOrden('actuacion')}
                      >
                        Última actuación
                      </button>
                    </div>
                  {/if}
                  <ul class="preview-lista">
                    <!-- La fuente puede devolver registros distintos con igual radicado y despacho. -->
                    {#each resultados as proceso}
                      <li class="preview-item">
                        <button
                          class="preview-resumen"
                          type="button"
                          aria-expanded={expansion?.idProceso === proceso.id_proceso}
                          disabled={!proceso.id_proceso}
                          onclick={() => toggleProcesoPreview(proceso.id_proceso)}
                        >
                          <span class="preview-info">
                            <span class="preview-radicado">{proceso.radicado}</span>
                            <span class="preview-despacho">{proceso.despacho}</span>
                            {#if proceso.departamento}
                              <span class="preview-meta">{proceso.departamento}</span>
                            {/if}
                            {#if proceso.fecha_ultima_actuacion}
                              <span class="preview-meta">
                                Última actuación publicada: {fecha(proceso.fecha_ultima_actuacion)}
                              </span>
                            {/if}
                          </span>
                          {#if proceso.id_proceso}
                            <i
                              class="bi {expansion?.idProceso === proceso.id_proceso
                                ? 'bi-chevron-up'
                                : 'bi-chevron-down'}"
                              aria-hidden="true"
                            ></i>
                          {/if}
                        </button>

                        {#if expansion && expansion.idProceso === proceso.id_proceso}
                          <div class="preview-expansion">
                            {#if expansion.loading}
                              <p class="estado-texto">Consultando actuaciones en la Rama Judicial…</p>
                            {:else if expansion.error}
                              <p class="estado-texto error">{expansion.error}</p>
                              <button
                                class="button button-ghost"
                                type="button"
                                onclick={reintentarExpansionPreview}
                              >
                                Reintentar
                              </button>
                            {:else}
                              {#if proceso.sujetos_procesales}
                                <div class="expansion-bloque">
                                  <p class="expansion-titulo">Involucrados</p>
                                  {@render involucradosProceso(proceso.sujetos_procesales)}
                                </div>
                              {/if}
                              {#if proceso.fecha_proceso}
                                <p class="preview-meta">
                                  Fecha del proceso: {fecha(proceso.fecha_proceso)}
                                </p>
                              {/if}
                              <div class="expansion-bloque">
                                <p class="expansion-titulo">
                                  Actuaciones{expansion.total ? ` (${expansion.total})` : ''}
                                </p>
                                {#if expansion.actuaciones.length === 0}
                                  <p class="estado-texto">Todavía no hay actuaciones publicadas.</p>
                                {:else}
                                  <ul class="actuaciones">
                                    {#each expansion.actuaciones as actuacion}
                                      <li class="actuacion">
                                        <div class="actuacion-fecha">{fecha(actuacion.fecha)}</div>
                                        <div class="actuacion-cuerpo">
                                          <div class="actuacion-tipo">
                                            {actuacion.actuacion || 'Actuación'}
                                          </div>
                                          {#if actuacion.anotacion}
                                            <div class="actuacion-anotacion">{actuacion.anotacion}</div>
                                          {/if}
                                        </div>
                                      </li>
                                    {/each}
                                  </ul>
                                  {#if expansion.pagina < expansion.paginas}
                                    <button
                                      class="button button-ghost"
                                      type="button"
                                      disabled={expansion.cargandoMas}
                                      onclick={cargarMasActuacionesPreview}
                                    >
                                      {expansion.cargandoMas
                                        ? 'Cargando…'
                                        : `Cargar más actuaciones (${expansion.total - expansion.actuaciones.length} restantes)`}
                                    </button>
                                  {/if}
                                {/if}
                              </div>

                              {#if alta.tipo === 'nombre'}
                                {@const yaSeguido = seguimientoDeRadicado(proceso.radicado_digitos)}
                                <div class="expansion-acciones">
                                  {#if yaSeguido}
                                    <button
                                      class="button button-secondary"
                                      type="button"
                                      onclick={() => navigate(`/seguimiento-judicial/seguimiento/${yaSeguido.id}`)}
                                    >
                                      <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                                      Ver seguimiento
                                    </button>
                                  {:else}
                                    <button
                                      class="button button-primary"
                                      type="button"
                                      disabled={alta.seguirBusy || !proceso.radicado_digitos}
                                      onclick={() => void seguirProcesoPreview(proceso.radicado_digitos, proceso.radicado)}
                                    >
                                      <i class="bi bi-bell-plus" aria-hidden="true"></i>
                                      {alta.seguirBusy ? 'Creando seguimiento…' : 'Seguir este proceso'}
                                    </button>
                                    <span class="expansion-nota">
                                      Crea un seguimiento por radicado con avisos de actuaciones nuevas.
                                    </span>
                                  {/if}
                                </div>
                                {#if alta.seguirError}
                                  <p class="estado-texto error">{alta.seguirError}</p>
                                {/if}
                              {/if}
                            {/if}
                          </div>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                  {#if resultados.length > 0 && (paginasResultados > 1 || esperandoMas)}
                    <div class="preview-paginacion">
                      <button
                        class="paginacion-flecha"
                        type="button"
                        disabled={paginaResultados <= 1 || alta.resultadosLoading}
                        aria-label="Página anterior"
                        onclick={() => irPaginaResultados(-1)}
                      >
                        <i class="bi bi-chevron-left" aria-hidden="true"></i>
                      </button>
                      <span class="paginacion-estado">
                        Página {paginaResultados} de {paginasResultados}
                      </span>
                      <button
                        class="paginacion-flecha"
                        type="button"
                        disabled={paginaResultados >= paginasResultados || alta.resultadosLoading}
                        aria-label="Página siguiente"
                        onclick={() => irPaginaResultados(1)}
                      >
                        <i class="bi bi-chevron-right" aria-hidden="true"></i>
                      </button>
                      <span class="paginacion-rango">
                        {inicioResultados}–{finResultados} de {totalResultados}
                      </span>
                      {#if esperandoMas}
                        <span class="paginacion-espera">Llegan más resultados…</span>
                      {/if}
                    </div>
                  {/if}
                {/if}
              {/if}

              {#if consulta.estado !== 'cancelada'}
                {#if activacion && !activacion.completada}
                  <div class="activacion-pendiente">
                    <p class="estado-texto">
                      <i class="bi bi-hourglass-split" aria-hidden="true"></i>
                      Preparando seguimiento · Completando la consulta inicial.
                    </p>
                    <p class="nota-proceso">
                      Avisaremos cuando quede activo; puedes cerrar esta ventana.
                    </p>
                    <div class="acciones-consulta">
                      <button
                        class="button button-ghost"
                        type="button"
                        disabled={alta.activarBusy}
                        onclick={cancelarActivacion}
                      >
                        Cancelar activación
                      </button>
                    </div>
                  </div>
                {:else if activacion?.completada}
                  <div class="confirmar-bloque">
                    <p class="estado-texto">Seguimiento activo.</p>
                    <button
                      class="button button-primary"
                      type="button"
                      onclick={() => navigate(`/seguimiento-judicial/seguimiento/${activacion.seguimiento_id}`)}
                    >
                      <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                      Ver seguimiento
                    </button>
                  </div>
                {:else}
                  <div class="confirmar-bloque">
                    <label class="check">
                      <input type="checkbox" bind:checked={alta.correoActivo} />
                      <span>Recibir avisos por correo</span>
                    </label>
                    <button
                      class="button button-primary"
                      type="button"
                      disabled={!puedeActivar()}
                      onclick={activarSeguimiento}
                    >
                      {#if alta.activarBusy}
                        Activando…
                      {:else}
                        <i class="bi bi-bell" aria-hidden="true"></i>
                        Activar seguimiento
                      {/if}
                    </button>
                  </div>
                  {#if consulta.en_curso || consulta.estado === 'parcial'}
                    <p class="nota-proceso">
                      La línea base se completa cuando termine la consulta; no
                      se avisará del histórico.
                    </p>
                  {/if}
                {/if}
                {#if alta.activarMensaje}
                  <p class="estado-texto">{alta.activarMensaje}</p>
                {/if}
                {#if alta.activarError}
                  <p class="estado-texto error">{alta.activarError}</p>
                {/if}
              {/if}
            {/if}
          </section>
        {/if}

        {#if listError}
          <p class="estado-texto error">{listError}</p>
        {/if}

        {#if listLoading}
          <p class="estado-texto">Cargando seguimientos…</p>
        {:else if seguimientos.length === 0}
          <div class="vacio">
            <p>Aún no tienes seguimientos.</p>
            <p>Usa “Seguir proceso” o “Vigilar nombre” para crear el primero.</p>
          </div>
        {:else}
          {@render listaSeguimientos(activeTab === 'procesos' ? deRadicado : deNombre)}
        {/if}
      {:else if route.name === 'seguimiento-detalle'}
        <!-- ─────────────── DETALLE ─────────────── -->
        {#if detalleLoading}
          <p class="estado-texto">Cargando el seguimiento…</p>
        {:else if detalleError && !detalle}
          <p class="estado-texto error">{detalleError}</p>
          <button class="button button-secondary" type="button" onclick={volver}>
            Volver
          </button>
        {:else if detalle}
          {@render detalleSeguimiento(detalle)}
        {/if}
      {/if}
    </div>
  </main>
</div>

<!-- ─────────────── Snippets ─────────────── -->

{#snippet chipEstado(seg: Seguimiento)}
  <span class="chip {estadoClase(seg)}">{estadoLabel(seg)}</span>
{/snippet}

{#snippet tarjetaSeguimiento(seg: Seguimiento)}
  <article class="card">
    <div class="card-head">
      <button
        class="card-titulo"
        type="button"
        onclick={() => navigate(`/seguimiento-judicial/seguimiento/${seg.id}`)}
      >
        {seg.etiqueta}
      </button>
      <div class="card-chips">
        {@render chipEstado(seg)}
        {#if seg.tipo === 'radicado'}
          <span class="chip neutro">{seg.resumen.procesos} proceso{seg.resumen.procesos === 1 ? '' : 's'}</span>
        {:else}
          <span class="chip neutro">{seg.resumen.coincidencias} coincidencia{seg.resumen.coincidencias === 1 ? '' : 's'}</span>
        {/if}
        {#if seg.resumen.novedades > 0}
          <span class="chip novedad">{seg.resumen.novedades} novedad{seg.resumen.novedades === 1 ? '' : 'es'}</span>
        {/if}
      </div>
    </div>

    <p class="card-estado">{estadoDetalle(seg)}</p>
    {#if seg.estado.ultimo_error && (seg.estado.fallos_consecutivos > 0 || seg.estado.revision_incompleta)}
      <p class="card-error" title={seg.estado.ultimo_error}>
        {seg.estado.ultimo_error.length > 160
          ? `${seg.estado.ultimo_error.slice(0, 160)}…`
          : seg.estado.ultimo_error}
      </p>
    {/if}

    <div class="card-acciones">
      <button
        class="button button-secondary"
        type="button"
        onclick={() => navigate(`/seguimiento-judicial/seguimiento/${seg.id}`)}
      >
        Ver detalle
      </button>
      <button
        class="button button-ghost"
        type="button"
        disabled={accionBusy === seg.id || seg.pausado}
        title={seg.pausado ? 'Reanuda el seguimiento para revisarlo' : 'Solicitar revisión'}
        onclick={() => revisar(seg)}
      >
        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
        Revisar ahora
      </button>
      <button
        class="button button-ghost"
        type="button"
        disabled={accionBusy === seg.id}
        onclick={() => togglePausa(seg)}
      >
        {seg.pausado ? 'Reanudar' : 'Pausar'}
      </button>
      <button
        class="button button-ghost"
        type="button"
        disabled={accionBusy === seg.id}
        onclick={() => toggleCorreo(seg)}
      >
        {seg.correo_activo ? 'Correo: sí' : 'Correo: no'}
      </button>
      <button
        class="button button-danger"
        type="button"
        disabled={accionBusy === seg.id}
        onclick={() => pedirRetiro(seg)}
      >
        Retirar
      </button>
    </div>
  </article>
{/snippet}

{#snippet listaSeguimientos(items: Seguimiento[])}
  {#if items.length === 0}
    <div class="vacio">
      <p>No hay seguimientos en esta pestaña.</p>
    </div>
  {:else}
    <div class="lista">
      {#each items as seg (seg.id)}
        {@render tarjetaSeguimiento(seg)}
      {/each}
    </div>
  {/if}
{/snippet}

{#snippet listaActuaciones()}
  {#if procesoLoading && !procesoDetalle}
    <p class="estado-texto">Cargando actuaciones…</p>
  {:else if procesoDetalle}
    {#if procesoDetalle.actuaciones.length === 0}
      <p class="estado-texto">Todavía no hay actuaciones publicadas.</p>
    {:else}
      <ul class="actuaciones">
        {#each procesoDetalle.actuaciones as actuacion (actuacion.id)}
          <li class="actuacion">
            <div class="actuacion-fecha">{fecha(actuacion.fecha)}</div>
            <div class="actuacion-cuerpo">
              <div class="actuacion-tipo">{actuacion.actuacion || 'Actuación'}</div>
              {#if actuacion.anotacion}
                <div class="actuacion-anotacion">{actuacion.anotacion}</div>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
      {#if procesoDetalle.pagina < procesoDetalle.paginas}
        <button
          class="button button-ghost"
          type="button"
          disabled={procesoLoading}
          onclick={cargarMasActuaciones}
        >
          {procesoLoading ? 'Cargando…' : 'Cargar más actuaciones'}
        </button>
      {/if}
    {/if}
  {/if}
{/snippet}

{#snippet involucradosProceso(texto: string)}
  <div class="involucrados">
    {#each agruparSujetosProcesales(texto) as grupo}
      <div class="involucrado-grupo">
        {#if grupo.rol}
          <span class="involucrado-rol">
            {grupo.rol}{grupo.nombres.length > 1 ? ` (${grupo.nombres.length})` : ''}
          </span>
        {/if}
        <div class="involucrado-nombres">
          {#each grupo.nombres as nombre}
            <span class="involucrado-nombre">{nombre}</span>
          {/each}
        </div>
      </div>
    {/each}
  </div>
{/snippet}

{#snippet detalleSeguimiento(seg: SeguimientoDetalle)}
  <div class="detalle-head">
    <button class="volver" type="button" onclick={volver}>
      <i class="bi bi-arrow-left" aria-hidden="true"></i>
      Volver
    </button>
    <h2>{seg.etiqueta}</h2>
    <div class="card-chips">
      {@render chipEstado(seg)}
      <span class="chip {seg.correo_activo ? 'ok' : 'off'}">
        {seg.correo_activo ? 'Correo activo' : 'Correo desactivado'}
      </span>
    </div>
  </div>

  <div class="detalle-estado">
    <p>{estadoDetalle(seg)}</p>
    {#if seg.estado.proxima_revision}
      <p class="meta">Próxima revisión: {fecha(seg.estado.proxima_revision)}</p>
    {/if}
    {#if seg.estado.ultimo_error}
      <p class="card-error">{seg.estado.ultimo_error}</p>
    {/if}
    <div class="card-acciones">
      <button
        class="button button-ghost"
        type="button"
        disabled={accionBusy === seg.id || seg.pausado}
        onclick={() => revisar(seg)}
      >
        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
        Revisar ahora
      </button>
      <button
        class="button button-ghost"
        type="button"
        disabled={accionBusy === seg.id}
        onclick={() => togglePausa(seg)}
      >
        {seg.pausado ? 'Reanudar' : 'Pausar'}
      </button>
      <button
        class="button button-ghost"
        type="button"
        disabled={accionBusy === seg.id}
        onclick={() => toggleCorreo(seg)}
      >
        {seg.correo_activo ? 'Desactivar correo' : 'Activar correo'}
      </button>
      <button
        class="button button-danger"
        type="button"
        disabled={accionBusy === seg.id}
        onclick={() => pedirRetiro(seg)}
      >
        Retirar seguimiento
      </button>
    </div>
  </div>

  {#if detalleError}
    <p class="estado-texto error">{detalleError}</p>
  {/if}

  {#if seg.tipo === 'radicado'}
    <section class="seccion">
      <h3>Procesos del radicado</h3>
      {#if !seg.procesos || seg.procesos.length === 0}
        <div class="vacio">
          <p>La Rama Judicial aún no publica procesos para este radicado.</p>
          <p>Si aparece uno, te avisaremos cuando se detecte.</p>
        </div>
      {:else}
        {#each seg.procesos as proceso (proceso.id)}
          <article class="proceso">
            <div class="proceso-head">
              <div>
                <div class="proceso-radicado">{proceso.radicado}</div>
                <div class="proceso-despacho">{proceso.despacho}</div>
              </div>
              <button
                class="button button-ghost"
                type="button"
                onclick={() => toggleProceso(proceso.id)}
              >
                {procesoAbierto === proceso.id ? 'Ocultar actuaciones' : 'Ver actuaciones'}
              </button>
            </div>
            <div class="proceso-meta">
              {#if proceso.departamento}<span>{proceso.departamento}</span>{/if}
              {#if proceso.fecha_proceso}
                <span>Radicado: {fecha(proceso.fecha_proceso)}</span>
              {/if}
              {#if proceso.fecha_ultima_actuacion}
                <span>Última actuación: {fecha(proceso.fecha_ultima_actuacion)}</span>
              {/if}
            </div>
            {#if proceso.sujetos_procesales}
              {@render involucradosProceso(proceso.sujetos_procesales)}
            {/if}
            {#if procesoAbierto === proceso.id}
              {@render listaActuaciones()}
            {/if}
          </article>
        {/each}
      {/if}
    </section>
  {:else}
    <section class="seccion">
      <h3>Coincidencias del nombre</h3>
      {#if seg.advertencia_homonimos}
        <p class="nota-homonimos">
          <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
          {seg.advertencia_homonimos}
        </p>
      {/if}
      {#if !seg.coincidencias || seg.coincidencias.length === 0}
        <div class="vacio">
          <p>No se han encontrado procesos con este nombre.</p>
          <p>Si aparece una coincidencia nueva, te avisaremos.</p>
        </div>
      {:else}
        {#each seg.coincidencias as coincidencia (coincidencia.proceso.id)}
          <article class="proceso">
            <div class="proceso-head">
              <div>
                <div class="proceso-radicado">{coincidencia.proceso.radicado}</div>
                <div class="proceso-despacho">{coincidencia.proceso.despacho}</div>
              </div>
              <div class="proceso-botones">
                <button
                  class="button button-ghost"
                  type="button"
                  onclick={() => toggleProceso(coincidencia.proceso.id)}
                >
                  {procesoAbierto === coincidencia.proceso.id
                    ? 'Ocultar actuaciones'
                    : 'Ver actuaciones'}
                </button>
                {#if coincidencia.ya_seguido}
                  <span class="chip ok">Siguiendo</span>
                {:else}
                  <button
                    class="button button-secondary"
                    type="button"
                    disabled={seguirBusy === coincidencia.proceso.id}
                    onclick={() => seguirEsteProceso(coincidencia)}
                  >
                    {seguirBusy === coincidencia.proceso.id
                      ? 'Activando…'
                      : 'Seguir este proceso'}
                  </button>
                {/if}
              </div>
            </div>
            <div class="proceso-meta">
              {#if coincidencia.proceso.fecha_ultima_actuacion}
                <span>Última actuación: {fecha(coincidencia.proceso.fecha_ultima_actuacion)}</span>
              {/if}
              {#if coincidencia.es_inicial}
                <span>Estaba en la búsqueda inicial</span>
              {:else if coincidencia.detectado_en}
                <span>Detectado: {fecha(coincidencia.detectado_en)}</span>
              {/if}
            </div>
            {#if coincidencia.proceso.sujetos_procesales}
              {@render involucradosProceso(coincidencia.proceso.sujetos_procesales)}
            {/if}
            {#if procesoAbierto === coincidencia.proceso.id}
              {@render listaActuaciones()}
            {/if}
          </article>
        {/each}
      {/if}
    </section>
  {/if}

  <section class="seccion">
    <h3>Novedades detectadas</h3>
    {#if seg.novedades.length === 0}
      <p class="estado-texto">Sin novedades por ahora.</p>
    {:else}
      <ul class="novedades">
        {#each seg.novedades as novedad (novedad.id)}
          <li class="novedad">
            <div class="novedad-tipo">{novedad.tipo_nombre}</div>
            <div class="novedad-resumen">{novedad.resumen}</div>
            <div class="novedad-fecha">{fecha(novedad.detectado_en)}</div>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
{/snippet}

<ConfirmModal
  open={confirmarRetiro.open}
  title="Retirar seguimiento"
  message="¿Deseas retirar este seguimiento? Dejarás de recibir avisos de sus novedades."
  confirmLabel="Retirar"
  cancelLabel="Cancelar"
  {palette}
  isDanger={true}
  onConfirm={confirmarRetiroAccion}
  onCancel={() => (confirmarRetiro = { open: false, id: null })}
/>

<style>
  .seguimiento-layout {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    height: 100%;
    background-color: var(--seg-surface);
    color: var(--seg-text);
  }

  .seguimiento-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px 16px 48px;
  }

  .seguimiento-container {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .aviso {
    background: color-mix(in srgb, var(--seg-accent) 12%, transparent);
    border: 1px solid color-mix(in srgb, var(--seg-accent) 38%, transparent);
    color: var(--seg-text);
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 14px;
  }

  .intro p {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 1.55;
    color: var(--seg-muted);
  }

  .eyebrow {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--seg-accent);
  }

  .tabs {
    display: flex;
    gap: 6px;
    border-bottom: 1px solid var(--seg-border);
  }

  .tab {
    border: none;
    background: transparent;
    color: var(--seg-muted);
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
  }

  .tab.active {
    color: var(--seg-text);
    border-bottom-color: var(--seg-accent);
  }

  .acciones-alta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .limite-nota {
    font-size: 13px;
    color: var(--seg-muted);
  }

  .button {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border-radius: 8px;
    border: 1px solid transparent;
    padding: 9px 14px;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.12s ease, background-color 0.12s ease;
  }

  .button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .button-primary {
    background: var(--seg-accent);
    color: #ffffff;
  }

  .button-primary:not(:disabled):hover {
    filter: brightness(1.06);
  }

  .button-secondary {
    background: transparent;
    color: var(--seg-text);
    border-color: var(--seg-border);
  }

  .button-secondary:not(:disabled):hover {
    background: color-mix(in srgb, var(--seg-accent) 10%, transparent);
  }

  .button-ghost {
    background: transparent;
    color: var(--seg-muted);
    border-color: transparent;
  }

  .button-ghost:not(:disabled):hover {
    color: var(--seg-text);
    background: color-mix(in srgb, var(--seg-accent) 10%, transparent);
  }

  .button-danger {
    background: transparent;
    color: #b91c1c;
    border-color: transparent;
  }

  .button-danger:not(:disabled):hover {
    background: rgba(185, 28, 28, 0.08);
  }

  .crear-panel {
    border: 1px solid var(--seg-border);
    border-radius: 12px;
    padding: 16px;
    background: color-mix(in srgb, var(--seg-input) 60%, transparent);
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .crear-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .crear-head h2 {
    margin: 0;
    font-size: 15px;
  }

  .cerrar {
    border: none;
    background: transparent;
    color: var(--seg-muted);
    cursor: pointer;
    font-size: 15px;
    padding: 4px;
  }

  .crear-form {
    display: flex;
    gap: 10px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1;
    min-width: 240px;
  }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--seg-muted);
  }

  .field input {
    background: var(--seg-input);
    color: var(--seg-text);
    border: 1px solid var(--seg-border);
    border-radius: 8px;
    padding: 9px 12px;
    font: inherit;
    font-size: 14px;
  }

  .nota-homonimos {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #8a6d3b;
    background: rgba(240, 166, 58, 0.12);
    border: 1px solid rgba(240, 166, 58, 0.35);
    border-radius: 8px;
    padding: 10px 12px;
  }

  .estado-texto {
    margin: 0;
    font-size: 14px;
    color: var(--seg-muted);
  }

  .estado-texto.error {
    color: #b91c1c;
  }

  .preview-lista {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .preview-paginacion {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .paginacion-flecha {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--seg-border);
    border-radius: 8px;
    background: var(--seg-surface);
    color: var(--seg-text);
    cursor: pointer;
  }

  .paginacion-flecha:hover:not(:disabled) {
    border-color: var(--seg-accent);
    color: var(--seg-accent);
  }

  .paginacion-flecha:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .paginacion-estado {
    font-size: 12.5px;
    color: var(--seg-text);
  }

  .paginacion-rango {
    margin-left: auto;
    font-size: 12px;
    color: var(--seg-muted);
  }

  .paginacion-espera {
    font-size: 12px;
    color: var(--seg-accent);
  }

  .acciones-consulta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .orden-selector {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .orden-etiqueta {
    font-size: 12px;
    font-weight: 600;
    color: var(--seg-muted);
    margin-right: 2px;
  }

  .orden-opcion {
    border: 1px solid var(--seg-border);
    background: transparent;
    color: var(--seg-muted);
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    cursor: pointer;
  }

  .orden-opcion.active {
    border-color: var(--seg-accent);
    color: var(--seg-accent);
    background: color-mix(in srgb, var(--seg-accent) 10%, transparent);
  }

  .activacion-pendiente {
    border: 1px dashed var(--seg-border);
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .preview-item {
    border: 1px solid var(--seg-border);
    border-radius: 8px;
    background: var(--seg-surface);
    overflow: hidden;
  }

  .preview-resumen {
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .preview-resumen:disabled {
    cursor: default;
  }

  .preview-resumen:hover:not(:disabled),
  .preview-resumen:focus-visible {
    background: color-mix(in srgb, var(--seg-accent) 6%, transparent);
    outline: none;
  }

  .preview-resumen i {
    flex: none;
    margin-top: 5px;
    font-size: 12px;
    color: var(--seg-muted);
  }

  .preview-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .preview-radicado {
    font-weight: 600;
    font-size: 14px;
  }

  .preview-despacho {
    font-size: 13px;
    color: var(--seg-text);
    margin-top: 2px;
  }

  .preview-meta {
    font-size: 12px;
    color: var(--seg-muted);
    margin-top: 3px;
  }

  .preview-expansion {
    border-top: 1px solid var(--seg-border);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .expansion-bloque {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .expansion-titulo {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--seg-muted);
  }

  .involucrados {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .involucrado-grupo {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .involucrado-rol {
    overflow-wrap: anywhere;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--seg-muted);
  }

  .involucrado-nombres {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .involucrado-nombre {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    overflow-wrap: anywhere;
    font-size: 12.5px;
    line-height: 1.35;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid var(--seg-border);
    background: color-mix(in srgb, var(--seg-accent) 7%, transparent);
    color: var(--seg-text);
  }

  .expansion-acciones {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .expansion-nota {
    font-size: 12px;
    color: var(--seg-muted);
  }

  .nota-proceso {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--seg-muted);
  }

  .confirmar-bloque {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  .check {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    color: var(--seg-text);
  }

  .vacio {
    border: 1px dashed var(--seg-border);
    border-radius: 12px;
    padding: 22px;
    text-align: center;
    color: var(--seg-muted);
    font-size: 14px;
  }

  .vacio p {
    margin: 4px 0;
  }

  .lista {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .card {
    border: 1px solid var(--seg-border);
    border-radius: 12px;
    padding: 15px 16px;
    background: var(--seg-input);
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .card-titulo {
    border: none;
    background: transparent;
    color: var(--seg-text);
    font: inherit;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    text-align: left;
    padding: 0;
  }

  .card-titulo:hover {
    color: var(--seg-accent);
  }

  .card-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .chip {
    font-size: 12px;
    font-weight: 600;
    border-radius: 999px;
    padding: 3px 10px;
    border: 1px solid transparent;
  }

  .chip.ok {
    color: #166534;
    background: rgba(34, 197, 94, 0.12);
    border-color: rgba(34, 197, 94, 0.35);
  }

  .chip.warn {
    color: #92400e;
    background: rgba(240, 166, 58, 0.15);
    border-color: rgba(240, 166, 58, 0.4);
  }

  .chip.off,
  .chip.neutro {
    color: var(--seg-muted);
    background: color-mix(in srgb, var(--seg-muted) 10%, transparent);
    border-color: var(--seg-border);
  }

  .chip.novedad {
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.12);
    border-color: rgba(59, 130, 246, 0.35);
  }

  .card-estado {
    margin: 0;
    font-size: 13px;
    color: var(--seg-muted);
  }

  .card-error {
    margin: 0;
    font-size: 12px;
    color: #b45309;
    background: rgba(240, 166, 58, 0.12);
    border-radius: 6px;
    padding: 8px 10px;
    line-height: 1.45;
  }

  .card-acciones {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .detalle-head {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .detalle-head h2 {
    margin: 0;
    font-size: 19px;
  }

  .volver {
    border: none;
    background: transparent;
    color: var(--seg-muted);
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 2px;
  }

  .volver:hover {
    color: var(--seg-accent);
  }

  .detalle-estado {
    border: 1px solid var(--seg-border);
    border-radius: 12px;
    padding: 14px 16px;
    background: var(--seg-input);
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 14px;
  }

  .detalle-estado p {
    margin: 0;
  }

  .detalle-estado .meta {
    font-size: 13px;
    color: var(--seg-muted);
  }

  .seccion {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .seccion h3 {
    margin: 6px 0 0;
    font-size: 15px;
  }

  .proceso {
    border: 1px solid var(--seg-border);
    border-radius: 12px;
    padding: 14px 16px;
    background: var(--seg-input);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .proceso-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .proceso-radicado {
    font-weight: 700;
    font-size: 14px;
  }

  .proceso-despacho {
    font-size: 13px;
    color: var(--seg-muted);
    margin-top: 2px;
  }

  .proceso-botones {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .proceso-meta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 12px;
    color: var(--seg-muted);
  }

  .actuaciones {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    max-height: 420px;
    overflow-y: auto;
  }

  .actuacion {
    display: flex;
    gap: 12px;
    padding: 9px 0;
    border-bottom: 1px solid var(--seg-border);
  }

  .actuacion:last-child {
    border-bottom: none;
  }

  .actuacion-fecha {
    min-width: 92px;
    font-size: 12px;
    color: var(--seg-muted);
    padding-top: 2px;
  }

  .actuacion-tipo {
    font-size: 13px;
    font-weight: 600;
  }

  .actuacion-anotacion {
    font-size: 12px;
    color: var(--seg-muted);
    margin-top: 3px;
    line-height: 1.5;
    white-space: pre-line;
  }

  .novedades {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .novedad {
    border: 1px solid var(--seg-border);
    border-left: 3px solid var(--seg-accent);
    border-radius: 8px;
    padding: 10px 12px;
    background: var(--seg-input);
  }

  .novedad-tipo {
    font-size: 12px;
    font-weight: 700;
    color: var(--seg-accent);
  }

  .novedad-resumen {
    font-size: 13px;
    margin-top: 3px;
    line-height: 1.45;
  }

  .novedad-fecha {
    font-size: 12px;
    color: var(--seg-muted);
    margin-top: 4px;
  }
</style>
